// Cross-route resume handoff for the connected Schedule wizard (lock G-05, decision P-22).
//
// The wizard used to carry `?template=<uuid>` in its address. That looks harmless and is not:
//
//   1. The raw row id lands in browser history, in the address bar over a supervisor's
//      shoulder, in any screenshot of the console, and in whatever a pasted link reaches. It
//      is a durable internal locator for an account-scoped row, handed out for free.
//   2. It invites a hand-typed or edited id. The wizard then has to answer for a pattern the
//      operator never chose in this session, on a step that assumes a loaded shape, which is
//      exactly the "Review a row nobody selected" defect the flow lock refuses.
//
// So the address keeps only what it can honestly own: the step, and the reviewed week. The
// resume target rides module state across exactly one navigation, mirroring the Timeclock
// map handoff already shipped in `punch-map-handoff.ts`.
//
// Deliberately NOT sessionStorage or localStorage: those keep the target on disk past the
// moment of use, for any later reader of the profile. Module state dies with the tab.
//
// Read-once by design. A resume that never reached the wizard cannot be replayed later, and a
// reload with no live context returns to a fresh pattern choice rather than a confident step
// about a row this session never loaded.

/** What one resume needs, and nothing more. */
export interface ScheduleWizardResume {
  /** The stored pattern to reload. Never rendered, never put in the address. */
  readonly templateId: string;
  /** The workspace the target belongs to, so a switch cannot carry it across tenants. */
  readonly accountId: string;
  /** Monday week start when the caller already chose one, otherwise null. */
  readonly weekStart: string | null;
}

let pending: ScheduleWizardResume | null = null;

/** Stage a resume target for the next wizard mount. Replaces any unread stash. */
export function stashWizardResume(resume: ScheduleWizardResume): void {
  pending = resume;
}

/**
 * Take the staged resume and clear it.
 *
 * The workspace is checked here rather than by the caller: the Schedule page does not remount
 * on an account switch or a support act-as, and one tenant's pattern must never resume inside
 * another tenant's wizard. A mismatch clears the stash instead of returning it, because a
 * target that belongs to a workspace we are no longer in is never useful later.
 */
export function takeWizardResume(accountId: string): ScheduleWizardResume | null {
  const staged = pending;
  pending = null;
  if (staged === null || staged.accountId !== accountId) {
    return null;
  }
  return staged;
}

/** Drop an unread stash, e.g. a navigation that left the wizard before it mounted. */
export function clearWizardResume(): void {
  pending = null;
}
